import { IconPlus } from '@tabler/icons-react'
import { useState } from 'react'

import { add, AstraButton, AstraIcon, AstraInputWrapper, AstraMultiSelect, AstraSelect, AstraTextField, getToday } from '~/astra'
import { getErrorMessageByStatusCode } from '~/hooks/tanstack-query'
import { useFlag } from '~/hooks/useFlag'
import { dispatchErrorToast } from '~/store/slices/toasterSlice'
import { useDepartments } from '~/storm'
import { getDayOvernightByLocationCareLevel } from '~/utils/dayOvernight'

import { DatePickerField } from '../../Form/DatePickerField'
import { useAppForm } from '../../Form/formHooks'
import { FieldGroupWrapper, FormContentWrapper, FormSectionWrapper } from '../../Form/FormWrapper'
import { ProfileCollapsibleCard } from '../../Form/ProfileCollapsibleCard'
import { createFormFieldMetaProps, createFormFieldProps } from '../../Form/utils'
import { defaultFormValues, defaultServicePeriod, type LocationFormDefaultValues, type LocationFormParsedValues, locationFormSchema } from './formState'
import { LocationProfileFields } from './LocationProfileFields'
import { LocationServicePeriodsFormFields } from './LocationServicePeriodsFormFields'

const careLevelOptions = [
    { label: getDayOvernightByLocationCareLevel('inpatient').label, value: 'inpatient' },
    { label: getDayOvernightByLocationCareLevel('outpatient').label, value: 'outpatient' },
] as const

export type OnSubmitFunction = (parsedValues: LocationFormParsedValues, initialValues: LocationFormDefaultValues) => Promise<void>

export type LocationFormProps = {
    initialValues: Partial<LocationFormParsedValues>
    onSubmit: OnSubmitFunction
    title: string
    isNew?: boolean
}

export const LocationForm = ({ initialValues, onSubmit, title, isNew = false }: LocationFormProps) => {
    const departments = useDepartments()
    const departmentOptions = departments.map(d => ({ label: d.name, value: d.id }))
    const isExpertEnabled = useFlag('hospital_configuration_expert')

    const [formDefaults, setFormDefaults] = useState<LocationFormDefaultValues>(() => ({
        ...defaultFormValues,
        ...initialValues,
        departments: initialValues.departments ?? defaultFormValues.departments,
        activeProfile: initialValues.activeProfile ?? defaultFormValues.activeProfile,
        futureProfiles: initialValues.futureProfiles ?? defaultFormValues.futureProfiles,
        servicePeriods: initialValues.servicePeriods ?? defaultFormValues.servicePeriods,
    }))

    const form = useAppForm({
        defaultValues: formDefaults,
        validators: { onChange: locationFormSchema },
        onSubmit: async ({ value, formApi }) => {
            const parsed = locationFormSchema.parse(value)
            try {
                await onSubmit(parsed, formDefaults)
                formApi.reset(parsed)
                setFormDefaults(parsed)
            } catch {
                dispatchErrorToast({ message: getErrorMessageByStatusCode() })
            }
        },
    })

    return (
        <FormContentWrapper title={title}>
            <div className="flex flex-col gap-10">
                <FormSectionWrapper title="Om stuen">
                    <FieldGroupWrapper>
                        <form.Field name="roomCode">
                            {field => (
                                <AstraTextField
                                    label="Stue navn"
                                    helperText={'Hentet fra DIPS'}
                                    value={field.state.value}
                                    onChange={e => field.handleChange(e.target.value)}
                                    placeholder="OP01"
                                    size="medium"
                                    fullWidth
                                    disabled
                                />
                            )}
                        </form.Field>
                        <form.Field name="dipsId">
                            {field => (
                                <AstraTextField
                                    label="DIPS ID"
                                    helperText={'Hentet fra DIPS'}
                                    value={field.state.value}
                                    onChange={e => field.handleChange(e.target.value)}
                                    size="medium"
                                    fullWidth
                                    disabled
                                />
                            )}
                        </form.Field>
                    </FieldGroupWrapper>

                    <FieldGroupWrapper>
                        {isExpertEnabled && (
                            <form.Field name="departments">
                                {field => (
                                    <AstraInputWrapper
                                        label="Avdelinger"
                                        borderless
                                        className="flex-1 pr-0"
                                        labelHtmlFor={field.name}
                                        fullWidth
                                        {...createFormFieldMetaProps(field)}
                                    >
                                        <AstraMultiSelect
                                            className="-ml-2"
                                            displayMode="inline"
                                            fullWidth
                                            options={departmentOptions}
                                            values={field.state.value}
                                            onChange={value => field.handleChange(value.map(v => v.value))}
                                        />
                                    </AstraInputWrapper>
                                )}
                            </form.Field>
                        )}
                        <form.Field name="defaultCareLevel">
                            {field => (
                                <AstraSelect
                                    label="Standard omsorgsnivå"
                                    options={careLevelOptions}
                                    value={field.state.value}
                                    onChange={value => field.handleChange(value)}
                                    {...createFormFieldMetaProps(field)}
                                />
                            )}
                        </form.Field>
                    </FieldGroupWrapper>
                </FormSectionWrapper>
                <FormSectionWrapper
                    noPadding
                    noBorder
                    title="Åpningstider"
                    description="Konfigurer åpningstider for stuen og når den er aktiv."
                    titleAction={
                        <AstraButton
                            variant="secondary"
                            type="button"
                            label="Legg til endring av åpningstid"
                            icon={<AstraIcon icon={IconPlus} />}
                            onClick={() => {
                                const previousServicePeriod = form.getFieldValue('servicePeriods').at(-1) ?? defaultServicePeriod
                                form.pushFieldValue('servicePeriods', {
                                    ...previousServicePeriod,
                                    isNewlyAdded: true,
                                    endDate: undefined,
                                    startDate: previousServicePeriod.endDate ? add(previousServicePeriod.endDate, 1, 'day') : undefined,
                                    originalServicePeriod: undefined,
                                })
                            }}
                        />
                    }
                >
                    <LocationServicePeriodsFormFields form={form} />
                </FormSectionWrapper>
                {isExpertEnabled && (
                    <>
                        <LocationProfileFields
                            form={form}
                            fields="activeProfile"
                            roomCodeLabel={`Operasjonstyper som kan gjennomføres på ${form.getFieldValue('roomCode')}`}
                        />
                        <FormSectionWrapper
                            title="Fremtidige stueprofiler"
                            noBorder
                            noPadding
                            description="Viser fremtidige endringer i stueprofilen."
                            titleAction={
                                <AstraButton
                                    variant="secondary"
                                    type="button"
                                    label="Legg til stueprofil"
                                    icon={<AstraIcon icon={IconPlus} />}
                                    onClick={() => {
                                        const lastFutureProfile = form.getFieldValue('futureProfiles').at(-1)
                                        const active = lastFutureProfile ?? form.getFieldValue('activeProfile')
                                        const newProfile = {
                                            ...active,
                                            id: undefined,
                                            activeFrom: undefined,
                                        }
                                        form.pushFieldValue('futureProfiles', newProfile)
                                    }}
                                />
                            }
                        >
                            <form.Field name="futureProfiles">
                                {profilesField =>
                                    profilesField.state.value.map((profile, index, arr) => (
                                        <form.Field name={`futureProfiles[${index}].activeFrom`} key={profile.id ?? index}>
                                            {field => (
                                                <ProfileCollapsibleCard
                                                    title="Fremtidig stueprofil"
                                                    activeFrom={field.state.value}
                                                    nextProfileActiveFrom={profilesField.state.value.at(index + 1)?.activeFrom ?? undefined}
                                                    defaultOpen={profile.id === undefined && index === arr.length - 1}
                                                    // reset the defaultOpen state when a new profile is added
                                                    key={arr.length}
                                                    onRemove={() => profilesField.removeValue(index)}
                                                >
                                                    <DatePickerField
                                                        label="Aktiv fra"
                                                        value={field.state.value}
                                                        startMonth={
                                                            form.getFieldValue('futureProfiles').at(index - 1)?.activeFrom ??
                                                            form.getFieldValue('activeProfile').activeFrom ??
                                                            getToday()
                                                        }
                                                        {...createFormFieldProps(field, { blurOnChange: true })}
                                                    />
                                                    <LocationProfileFields
                                                        form={form}
                                                        fields={`futureProfiles[${index}]`}
                                                        roomCodeLabel={`Operasjonstyper som kan gjennomføres på ${form.getFieldValue('roomCode')}`}
                                                        size="compact"
                                                    />
                                                </ProfileCollapsibleCard>
                                            )}
                                        </form.Field>
                                    ))
                                }
                            </form.Field>
                        </FormSectionWrapper>
                    </>
                )}
            </div>

            <form.AppForm>{isNew ? <form.FormSaveNewControl /> : <form.FormFloatingControl />}</form.AppForm>
        </FormContentWrapper>
    )
}
